import React, { useState } from 'react';
import SwipeableModal from '../../../components/SwipeableModal';
import { X, AlertTriangle } from 'lucide-react';

const CancelSubscriptionModal = ({ isOpen, onClose, onConfirm, currentPlan, loading }) => {
  const [cancellationReason, setCancellationReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [step, setStep] = useState(1);

  const cancellationReasons = [
    'Too expensive',
    'Not using the features',
    'Found a better alternative',
    'Technical issues',
    'Temporary break',
    'Other'
  ];

  const handleConfirmCancel = () => {
    onConfirm({ reason: cancellationReason, feedback });
  };

  const handleClose = () => {
    setCancellationReason('');
    setFeedback('');
    setStep(1);
    onClose();
  };

  return (
    <SwipeableModal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 1 ? 'Cancel Subscription' : 'Confirm Cancellation'}
      maxWidth="max-w-2xl"
      loading={loading}
    >
      {step === 1 ? (
        <>
          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Before you go...</h3>
              <p className="text-sm text-amber-800">
                Your subscription will remain active until the end of your current billing period.
                You'll lose access to premium features after that.
              </p>
            </div>
          </div>

          {/* Current Plan Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">Current Plan: {currentPlan?.name}</h3>
            <p className="text-sm text-gray-600 mb-3">
              You'll lose access to:
            </p>
            <ul className="space-y-2">
              {currentPlan?.features?.advanced_analytics && (
                <li className="flex items-center text-sm text-gray-700">
                  <X className="w-4 h-4 text-red-500 mr-2" />
                  Advanced symptom analytics
                </li>
              )}
              {currentPlan?.features?.caregiver_management && (
                <li className="flex items-center text-sm text-gray-700">
                  <X className="w-4 h-4 text-red-500 mr-2" />
                  Caregiver management
                </li>
              )}
              {currentPlan?.features?.unlimited_exports && (
                <li className="flex items-center text-sm text-gray-700">
                  <X className="w-4 h-4 text-red-500 mr-2" />
                  Unlimited exports
                </li>
              )}
              {currentPlan?.features?.priority_support && (
                <li className="flex items-center text-sm text-gray-700">
                  <X className="w-4 h-4 text-red-500 mr-2" />
                  Priority support
                </li>
              )}
            </ul>
          </div>

          {/* Cancellation Reason */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Why are you cancelling? (Optional)
            </label>
            <div className="space-y-2">
              {cancellationReasons?.map((reason) => (
                <label
                  key={reason}
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="radio"
                    name="cancellation-reason"
                    value={reason}
                    checked={cancellationReason === reason}
                    onChange={(e) => setCancellationReason(e?.target?.value)}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700">{reason}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Feedback */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional feedback (Optional)
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e?.target?.value)}
              placeholder="Tell us how we can improve..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium min-h-[44px] touch-manipulation active:scale-95"
              disabled={loading}
            >
              Keep Subscription
            </button>
            <button
              onClick={() => setStep(2)}
              className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium min-h-[44px] touch-manipulation active:scale-95"
              disabled={loading}
            >
              Continue to Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Final Confirmation */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Are you absolutely sure?
            </h3>
            <p className="text-gray-600">
              This action will cancel your {currentPlan?.name} subscription.
              You'll still have access until your current billing period ends.
            </p>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Current Plan:</span>
                <span className="font-medium text-gray-900">{currentPlan?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Access Until:</span>
                <span className="font-medium text-gray-900">
                  {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toLocaleDateString()}
                </span>
              </div>
              {cancellationReason && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Reason:</span>
                  <span className="font-medium text-gray-900">{cancellationReason}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => setStep(1)}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium min-h-[44px] touch-manipulation active:scale-95"
              disabled={loading}
            >
              Go Back
            </button>
            <button
              onClick={handleConfirmCancel}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation active:scale-95"
            >
              {loading ? 'Cancelling...' : 'Confirm Cancellation'}
            </button>
          </div>
        </>
      )}
    </SwipeableModal>
  );
};

export default CancelSubscriptionModal;